// src/components/AGTFit/workout/NewRoutineDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  TextField,
  Box,
  IconButton,
  Grid,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  X,
  Plus,
  Trash2,
  Dumbbell,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import RoutineExerciseExplorer from '../explorer/RoutineExerciseExplorer';
import agtfitService from '../../service/agtfitService';

// Color principal
const FIT_ORANGE = '#FF6B00';

// Colores para modo wallpaper
const WALLPAPER_COLORS = {
  bg: 'rgba(128, 128, 128, 0.3)',
  bgLight: 'rgba(128, 128, 128, 0.25)',
  border: 'rgba(128, 128, 128, 0.3)',
  text: '#FFFFFF',
  textSecondary: 'rgba(255, 255, 255, 0.7)'
};

// Estilo para diálogos estilo Apple como en ExerciseExplorer
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    [theme.breakpoints.up('sm')]: {
      borderRadius: '20px',
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '0px',
      margin: 0, // Eliminar márgenes en móvil
      maxHeight: '100%', // Ocupar toda la altura
      height: '100%', // Forzar altura completa
    },
    boxShadow: theme.palette.actualMode === 'wallpaper'
      ? 'none'
      : (theme.palette.mode === 'dark'
        ? '0 20px 60px rgba(0, 0, 0, 0.5)'
        : '0 20px 60px rgba(0, 0, 0, 0.2)'),
    background: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bg
      : (theme.palette.mode === 'dark'
        ? 'rgba(30, 30, 32, 0.95)'
        : 'rgba(255, 255, 255, 1)'),
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
    overflow: 'hidden',
    zIndex: theme.zIndex.modal + 1, // Z-index menor que el explorador de ejercicios
  },
  // Asegurar que el diálogo está por encima de todo
  zIndex: theme.zIndex.modal + 1
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  boxShadow: 'none',
  backgroundImage: 'linear-gradient(90deg, #FF9800 0%, #FF6B00 100%)',
  color: '#FFFFFF',
  '&:hover': {
    backgroundImage: 'linear-gradient(90deg, #FF8800 0%, #FF5500 100%)',
    boxShadow: 'none',
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '15px',
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(235, 235, 240, 0.8)'),
  }
}));

// Campo de texto estilizado
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? WALLPAPER_COLORS.bgLight
      : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
    backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
    '& fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper' ? WALLPAPER_COLORS.border : 'transparent',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.actualMode === 'wallpaper'
        ? WALLPAPER_COLORS.border
        : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'),
    },
    '&.Mui-focused fieldset': {
      borderColor: FIT_ORANGE,
    },
  }
}));

// Unused styled component - StyledSelect
// const StyledSelect = styled(Select)(({ theme }) => ({
//   borderRadius: 12,
//   backgroundColor: theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)',
//   '& .MuiOutlinedInput-notchedOutline': {
//     borderColor: 'transparent',
//   },
//   '&:hover .MuiOutlinedInput-notchedOutline': {
//     borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)',
//   },
//   '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//     borderColor: FIT_ORANGE,
//   },
// }));

// Estilos para los elementos de la lista de ejercicios
const ExerciseListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(1),
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? WALLPAPER_COLORS.bgLight
    : (theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.8)' : 'rgba(245, 245, 250, 0.8)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  '&:hover': {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(255,107,0,0.15)'
      : (theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(235, 235, 240, 0.8)'),
  }
}));

const IconBox = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(255, 107, 0, 0.2)'
    : (theme.palette.mode === 'dark' ? 'rgba(255, 107, 0, 0.15)' : 'rgba(255, 107, 0, 0.1)'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(8px)' : 'none',
  border: theme.palette.actualMode === 'wallpaper' ? `1px solid ${WALLPAPER_COLORS.border}` : 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  color: FIT_ORANGE,
  flexShrink: 0,
  minWidth: 40,
  minHeight: 40,
}));

const NewRoutineDialog = ({ open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Estado para el formulario
  const [routineName, setRoutineName] = useState('');
  const [routineDescription, setRoutineDescription] = useState('');
  const [exercises, setExercises] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Estado para el explorador de ejercicios
  const [openExerciseExplorer, setOpenExerciseExplorer] = useState(false);
  
  // Manejar cierre del diálogo
  const handleClose = () => {
    onClose();
    // Resetear estado
    setRoutineName('');
    setRoutineDescription('');
    setExercises([]);
  };
  
  // Abrir explorador de ejercicios
  const handleOpenExerciseExplorer = () => {
    setOpenExerciseExplorer(true);
  };
  
  // Manejar selección múltiple de ejercicios desde el explorador
  const handleAddSelectedExercises = (selectedExercises) => {
    setOpenExerciseExplorer(false);
    
    // Añadir ejercicios a la lista con valores por defecto
    const newExercises = selectedExercises.map(exercise => ({
      ...exercise,
      sets: 3,
      reps: 10,
      weight: 0,
      restTime: 60, // segundos
      notes: ''
    }));
    
    setExercises([...exercises, ...newExercises]);
  };
  
  // Actualizar configuración de un ejercicio
  const updateExercise = (index, field, value) => {
    const updatedExercises = [...exercises];
    updatedExercises[index] = {
      ...updatedExercises[index],
      [field]: value
    };
    setExercises(updatedExercises);
  };
  
  // Eliminar un ejercicio
  const removeExercise = (index) => {
    const updatedExercises = [...exercises];
    updatedExercises.splice(index, 1);
    setExercises(updatedExercises);
  };
  
  // Mover un ejercicio hacia arriba en la lista
  const moveExerciseUp = (index) => {
    if (index === 0) return;
    const updatedExercises = [...exercises];
    const temp = updatedExercises[index];
    updatedExercises[index] = updatedExercises[index - 1];
    updatedExercises[index - 1] = temp;
    setExercises(updatedExercises);
  };
  
  // Mover un ejercicio hacia abajo en la lista
  const moveExerciseDown = (index) => {
    if (index === exercises.length - 1) return;
    const updatedExercises = [...exercises];
    const temp = updatedExercises[index];
    updatedExercises[index] = updatedExercises[index + 1];
    updatedExercises[index + 1] = temp;
    setExercises(updatedExercises);
  };
  
  // Guardar la rutina
  const handleSaveRoutine = async () => {
    if (!routineName.trim()) {
        // Mostrar error - nombre requerido
        return;
    }
    
    if (exercises.length === 0) {
        // Mostrar error - al menos un ejercicio
        return;
    }
    
    setSubmitting(true);
    
    try {
        // Preparar datos de la rutina
        const routineData = {
        name: routineName.trim(),
        description: routineDescription.trim(),
        exercises: exercises.map(exercise => ({
            exerciseId: exercise.exerciseId,
            name: exercise.name,
            sets: exercise.sets,
            reps: exercise.reps,
            weight: exercise.weight,
            restTime: exercise.restTime,
            notes: exercise.notes,
            primaryMuscle: exercise.primaryMuscle,
            imageUrl: exercise.imageUrl,
            videoUrl: exercise.videoUrl
        }))
        };
    
        // Realizar la llamada al servicio para guardar la rutina
        const response = await agtfitService.createRoutine(routineData);
    
        if (response.success) {
        // Cerrar diálogo con éxito
        onClose(true, response.data);
        } else {
        console.error('Error saving routine:', response.error);
        // Aquí podrías mostrar un mensaje de error al usuario
        }
    } catch (error) {
        console.error('Exception while saving routine:', error);
        // Aquí podrías mostrar un mensaje de error al usuario
    } finally {
        setSubmitting(false);
    }
    };
  
  // Renderizar configuración de un ejercicio
  const renderExerciseConfig = (exercise, index) => {
    return (
      <ExerciseListItem key={index} disablePadding>
        <Box sx={{ p: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <IconBox>
              <Dumbbell size={20} />
            </IconBox>
            <Typography variant="subtitle1" fontWeight={500}>
              {exercise.name}
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <IconButton 
              size="small" 
              onClick={() => moveExerciseUp(index)}
              disabled={index === 0}
            >
              <ChevronUp size={18} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => moveExerciseDown(index)}
              disabled={index === exercises.length - 1}
            >
              <ChevronDown size={18} />
            </IconButton>
            <IconButton 
              size="small" 
              onClick={() => removeExercise(index)}
            >
              <Trash2 size={18} />
            </IconButton>
          </Box>
          
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <StyledTextField
                label="Sets"
                type="number"
                value={exercise.sets}
                onChange={(e) => updateExercise(index, 'sets', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 1, max: 10 } }}
              />
            </Grid>
            <Grid item xs={4}>
              <StyledTextField
                label="Reps"
                type="number"
                value={exercise.reps}
                onChange={(e) => updateExercise(index, 'reps', parseInt(e.target.value) || 0)}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 1, max: 100 } }}
              />
            </Grid>
            <Grid item xs={4}>
              <StyledTextField
                label="Weight (kg)"
                type="number"
                value={exercise.weight}
                onChange={(e) => updateExercise(index, 'weight', parseFloat(e.target.value) || 0)}
                fullWidth
                size="small"
                InputProps={{ inputProps: { min: 0, step: 0.5 } }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2 }}>
            <StyledTextField
              label="Rest Time"
              type="number"
              value={exercise.restTime}
              onChange={(e) => updateExercise(index, 'restTime', parseInt(e.target.value) || 0)}
              fullWidth
              size="small"
              InputProps={{
                endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                inputProps: { min: 0, max: 300 }
              }}
            />
          </Box>
          
          <Box sx={{ mt: 2 }}>
            <StyledTextField
              label="Notes"
              value={exercise.notes}
              onChange={(e) => updateExercise(index, 'notes', e.target.value)}
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </Box>
      </ExerciseListItem>
    );
  };
  
  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="md"
      >
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)'}`
        }}>
          <Typography variant="h5" fontWeight={600}>
            Create Routine
          </Typography>
          
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(60, 60, 62, 0.8)' : 'rgba(240, 240, 245, 0.8)',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(70, 70, 72, 0.8)' : 'rgba(230, 230, 235, 0.8)',
              }
            }}
          >
            <X size={20} />
          </IconButton>
        </Box>
        
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StyledTextField
                label="Routine Name"
                value={routineName}
                onChange={(e) => setRoutineName(e.target.value)}
                fullWidth
                required
                placeholder="e.g. Upper Body Workout"
              />
            </Grid>
            
            <Grid item xs={12}>
              <StyledTextField
                label="Description (optional)"
                value={routineDescription}
                onChange={(e) => setRoutineDescription(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="Add details about this routine"
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Exercises
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add exercises to your routine and configure sets, reps, and rest time
            </Typography>
            
            <SecondaryButton
              fullWidth
              startIcon={<Plus size={18} />}
              onClick={handleOpenExerciseExplorer}
              sx={{ mb: 3 }}
            >
              Add Exercise
            </SecondaryButton>
            
            {exercises.length > 0 ? (
              <List sx={{ py: 0 }}>
                {exercises.map((exercise, index) => renderExerciseConfig(exercise, index))}
              </List>
            ) : (
              <Box 
                sx={{ 
                  textAlign: 'center', 
                  py: 6,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(50, 50, 52, 0.4)' : 'rgba(245, 245, 250, 0.4)',
                  borderRadius: 3,
                  border: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                }}
              >
                <Dumbbell size={40} color={theme.palette.text.secondary} opacity={0.5} />
                <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                  No exercises added yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Click "Add Exercise" to build your routine
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={handleClose} 
            sx={{ 
              color: theme.palette.text.secondary,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '15px',
            }}
          >
            Cancel
          </Button>
          <ActionButton 
            onClick={handleSaveRoutine}
            disabled={submitting || !routineName.trim() || exercises.length === 0}
          >
            {submitting ? 'Saving...' : 'Save Routine'}
          </ActionButton>
        </DialogActions>
      </StyledDialog>
      
      {/* Explorador de ejercicios específico para rutinas */}
      <RoutineExerciseExplorer
        open={openExerciseExplorer}
        onClose={() => setOpenExerciseExplorer(false)}
        onAddSelectedExercises={handleAddSelectedExercises}
      />
    </>
  );
};

export default NewRoutineDialog;